import { useRef, useState, useCallback, useEffect } from "react";

export const useDrawerResize = (isOpen: boolean, position: "start" | "end" | "bottom") => {
    const animationFrame = useRef<number>(0);
    const drawerRef = useRef<HTMLDivElement>(null);
    const [isResizing, setIsResizing] = useState(false);
    const [drawerWidth, setDrawerWidth] = useState<number | undefined>(undefined);

    const startResizing = useCallback(() => setIsResizing(true), []);
    const stopResizing = useCallback(() => setIsResizing(false), []);

    const resize = useCallback(
        ({ clientX }: MouseEvent) => {
            animationFrame.current = requestAnimationFrame(() => {
                if (isResizing && drawerRef.current) {
                    if (position === "end") {
                        setDrawerWidth(drawerRef.current.getBoundingClientRect().right - clientX);
                    } else {
                        setDrawerWidth(clientX - drawerRef.current.getBoundingClientRect().left);
                    }
                    window.getSelection()?.removeAllRanges();
                }
            });
        },
        [isResizing, position]
    );

    const onKeyDown = useCallback(
        (event: React.KeyboardEvent<HTMLDivElement>) => {
            if (!drawerRef.current) {
                return;
            }
            if ((event.key === "ArrowLeft" && position === "end") || (event.key === "ArrowRight" && position === "start")) {
                setDrawerWidth(drawerRef.current.clientWidth + 10);
            } else if ((event.key === "ArrowRight" && position === "end") || (event.key === "ArrowLeft" && position === "start")) {
                setDrawerWidth(drawerRef.current.clientWidth - 10);
            }
        },
        [position]
    );

    useEffect(() => {
        if (isOpen) {
            window.addEventListener("mousemove", resize);
            window.addEventListener("mouseup", stopResizing);
        }

        return () => {
            cancelAnimationFrame(animationFrame.current);
            window.removeEventListener("mousemove", resize);
            window.removeEventListener("mouseup", stopResizing);
        };
    }, [resize, stopResizing, isOpen]);

    return {
        drawerRef,
        drawerWidth,
        isResizing,
        startResizing,
        onKeyDown
    };
};
